import { apiJson } from './client';

export type AuthType = 'none' | 'local';

export type AuthUser = {
    id: number;
    username: string;
    real_name: string | null;
    email: string | null;
    telegram?: string | null;
};

export type AuthStatus = {
    authenticated: boolean;
    auth_type: AuthType;

    user: AuthUser | null;

    roles: string[];
    permissions: string[];

    is_admin: boolean;
};

export type AuthResponse = {
    success: boolean;
    auth: AuthStatus;
};

export type LoginPayload = {
    login: string;
    password: string;
};

export type RegisterPayload = {
    username: string;
    password: string;
    real_name: string;
    email: string;
    telegram?: string;
};

export type RegisterResponse = {
    success: boolean;
    message: string;
};

export const EMPTY_AUTH: AuthStatus = {
    authenticated: false,
    auth_type: 'none',

    user: null,

    roles: [],
    permissions: [],

    is_admin: false,
};

function normalizeAuthStatus(auth: Partial<AuthStatus> | null | undefined): AuthStatus {
    return {
        authenticated: auth?.authenticated === true,
        auth_type: auth?.auth_type ?? 'none',
        user: auth?.user ?? null,
        roles: auth?.roles ?? [],
        permissions: auth?.permissions ?? [],
        is_admin: auth?.is_admin === true,
    };
}

function unwrapAuthResponse(response: AuthResponse): AuthStatus {
    return normalizeAuthStatus(response.auth);
}

export async function loadAuthStatus(apiBaseUrl: string): Promise<AuthStatus> {
    try {
        const response = await apiJson<AuthResponse>(apiBaseUrl, '/auth/me');
        return unwrapAuthResponse(response);
    } catch {
        return EMPTY_AUTH;
    }
}

export async function loginUser(
    apiBaseUrl: string,
    payload: LoginPayload,
): Promise<AuthStatus> {
    const response = await apiJson<AuthResponse>(apiBaseUrl, '/auth/login', {
        method: 'POST',
        body: JSON.stringify(payload),
    });

    return unwrapAuthResponse(response);
}

export async function registerUser(
    apiBaseUrl: string,
    payload: RegisterPayload,
): Promise<RegisterResponse> {
    return apiJson<RegisterResponse>(apiBaseUrl, '/auth/register', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function logoutUser(apiBaseUrl: string): Promise<void> {
    await apiJson(apiBaseUrl, '/auth/logout', {
        method: 'POST',
    });
}

export function hasRole(auth: AuthStatus, role: string): boolean {
    return auth.roles.includes(role);
}

export function hasPermission(auth: AuthStatus, permission: string): boolean {
    return auth.permissions.includes('*') || auth.permissions.includes(permission);
}

export function canViewCalendar(auth: AuthStatus, calendar: string): boolean {
    return hasPermission(auth, `${calendar}.view`);
}

export function canRespondCalendar(auth: AuthStatus, calendar: string): boolean {
    return hasPermission(auth, `${calendar}.respond`);
}

export function canWriteCalendar(auth: AuthStatus, calendar: string): boolean {
    return hasPermission(auth, `${calendar}.write`);
}
